import { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import type { Role } from "@/modules/roles";
import { RoleSummary } from "./role-summary";
import styles from "./delete-role.module.scss";

export const DELETE_ROLE_TITLE = "Excluir Perfil";
export const DELETE_ROLE_DESCRIPTION = "Essa ação remove permanentemente o perfil do sistema.";
/** MS001 — alerta de exclusão permanente, apresentado antes da confirmação. */
export const DELETE_ROLE_WARNING =
  "Atenção: essa ação não pode ser desfeita. O perfil será removido permanentemente e não poderá ser recuperado.";

interface DeleteRoleDialogProps {
  role: Role;
  /**
   * Executa a exclusão. Cabe ao chamador comunicar o resultado e fechar o
   * pop-up no sucesso — em caso de falha o pop-up segue aberto.
   * A promessa não deve ser rejeitada.
   */
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export function DeleteRoleDialog({ role, onConfirm, onCancel }: DeleteRoleDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  // RN01: a exclusão só parte daqui e, enquanto a requisição não volta, o botão
  // fica bloqueado — uma confirmação envia exatamente uma requisição.
  async function handleConfirm() {
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
    }
  }

  const header = (
    <span className={styles.header}>
      <span className={styles.headerIcon} aria-hidden>
        <i className="pi pi-trash" />
      </span>
      <span className={styles.headerText}>
        <span className={styles.headerTitle}>{DELETE_ROLE_TITLE}</span>
        <span className={styles.headerDescription}>{DELETE_ROLE_DESCRIPTION}</span>
      </span>
    </span>
  );

  const footer = (
    <div className={styles.actions}>
      <Button
        type="button"
        className={styles.cancelButton}
        label="Cancelar"
        severity="secondary"
        text
        onClick={onCancel}
        disabled={isDeleting}
        data-testid="delete-role-cancel"
      />
      <Button
        type="button"
        className={styles.confirmButton}
        label={DELETE_ROLE_TITLE}
        icon="pi pi-trash"
        severity="danger"
        onClick={handleConfirm}
        loading={isDeleting}
        data-testid="delete-role-confirm"
      />
    </div>
  );

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      header={header}
      footer={footer}
      visible
      modal
      draggable={false}
      resizable={false}
      // A exclusão em curso não pode ser interrompida pelo X nem pelo Esc.
      closable={!isDeleting}
      closeOnEscape={!isDeleting}
      ariaCloseIconLabel="Fechar"
      onHide={onCancel}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="delete-role-dialog"
    >
      <RoleSummary role={role} testId="delete-role-summary" />

      <div className={styles.warning} role="alert" data-testid="delete-role-warning">
        <i className={`pi pi-exclamation-triangle ${styles.warningIcon}`} aria-hidden />
        <p className={styles.warningText}>{DELETE_ROLE_WARNING}</p>
      </div>
    </Dialog>
  );
}
